import en from './en.json';
import sq from './sq.json';

/**
 * Every visible text comes from en.json (GOLDEN-RULE.md, rule 1). Each other language is a file
 * next to it with exactly the same keys (checked by test/i18n.test.ts). A device keeps the
 * language chosen on it; until someone chooses, it follows the browser or phone. Server
 * administration turns languages on per business: inside a business (its app and its public
 * pages) only those are offered, and its own language is used when the device's is not one.
 */
export type MessageKey = keyof typeof en;
export type Lang = 'en' | 'sq';

/** Each language under its own name, the way its speakers look for it. */
export const LANGUAGES: { code: Lang; name: string; locale: string }[] = [
  { code: 'en', name: 'English', locale: 'en-GB' },
  { code: 'sq', name: 'Shqip', locale: 'sq-AL' },
];

const CATALOGS: Record<Lang, Record<string, string>> = { en, sq };
const KEY = 'erp.lang';
const ALL = LANGUAGES.map((l) => l.code);

/** The known languages of a list, in its order; all of them when the list says nothing. */
const known = (list: readonly string[] | null | undefined): Lang[] => {
  const out = (list ?? []).filter((c, i, a): c is Lang => c in CATALOGS && a.indexOf(c) === i);
  return out.length ? out : list && list.length ? ['en'] : ALL;
};

/** What this device chose, or what its browser or phone speaks, among the languages given. */
function pick(allowed: Lang[]): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && allowed.includes(saved as Lang)) return saved as Lang;
  } catch {
    // No storage: follow the browser.
  }
  const nav = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  for (const l of nav) {
    const code = l?.slice(0, 2).toLowerCase() as Lang;
    if (code && allowed.includes(code)) return code;
  }
  return allowed[0]!;
}

/** The business this device last signed in to, with its languages (kept by lib/session.tsx). */
function savedTenantLanguages(): string[] | null {
  try {
    return (JSON.parse(localStorage.getItem('erp.tenant') ?? 'null') as { languages?: string[] } | null)?.languages ?? null;
  } catch {
    return null;
  }
}

let allowed: Lang[] = ALL;
let lang: Lang = pick(allowed);
let messages = CATALOGS[lang];
if (typeof document !== 'undefined') document.documentElement.lang = lang;

const use = (next: Lang) => {
  lang = next;
  messages = CATALOGS[next];
  if (typeof document !== 'undefined') document.documentElement.lang = next;
};

/**
 * Limits the languages to those a business has. Returns true when the page was showing another
 * one and has switched: what is already on screen must then be drawn again.
 */
export function allowLanguages(list: readonly string[] | null | undefined): boolean {
  allowed = known(list);
  // Chosen again among these: a device that speaks none of them gets the business's own one.
  const next = pick(allowed);
  if (next === lang) return false;
  use(next);
  return true;
}

/** Inside the staff app, before anything shows: the languages of the business last used here. */
export function allowSavedTenantLanguages() {
  const list = savedTenantLanguages();
  if (list) allowLanguages(list);
}

/** The languages offered here, each under its own name. */
export const availableLanguages = () => LANGUAGES.filter((l) => allowed.includes(l.code));

export const currentLang = (): Lang => lang;
let formatLocale: string | null = null;
/**
 * Writes dates and numbers the way the business's country does (its country pack: "en-US" for
 * 11/1/2026, "en-DE" for 1.234,56). Only while the screen is in English; another language keeps
 * its own.
 */
export function setFormatLocale(next: string | null) {
  formatLocale = next && /^[a-z]{2}(-[A-Z0-9]{2,3})?$/.test(next) ? next : null;
}

/** The locale for dates and numbers, e.g. "sq-AL", or the business's country formats in English. */
export const locale = (): string => (lang === 'en' && formatLocale ? formatLocale : LANGUAGES.find((l) => l.code === lang)!.locale);

/** Keeps the choice on this device and shows every page again in that language. */
export function setLang(next: Lang) {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Private mode: the choice lasts until the page loads again.
  }
  if (next === lang) return;
  use(next);
  if (typeof window !== 'undefined') window.location.reload();
}

// A partner's brand (white-label, lib/brand.ts) takes the place of "ERP PRO" in everything said.
let brandName: string | null = null;
export const setBrandName = (name: string | null) => {
  brandName = name;
};
export const brandText = (text: string) => (brandName ? text.replaceAll('ERP PRO', brandName) : text);

export function t(key: MessageKey, vars: Record<string, string | number> = {}): string {
  const text = messages[key] ?? (en as Record<string, string>)[key];
  if (text === undefined) return key;
  return brandText(text.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`)));
}

/** Picks `key.one` or `key.other` by count. */
export function tn(key: string, n: number, vars: Record<string, string | number> = {}): string {
  return t(`${key}.${n === 1 ? 'one' : 'other'}` as MessageKey, { n, ...vars });
}
